import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { toast } from "react-hot-toast";
import {
  Shield,
  BarChart3,
  Users,
  Map,
  Settings,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Eye,
  Trash2,
  MessageSquare,
  Globe,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  getAllUsers,
  banUser,
  deleteUser,
  getAllMapsForAdmin,
  approveMap,
  rejectMap,
  getSystemStats,
  updateUserRole,
} from "../../services/adminService";
import { User, MapData } from "../../types";
import InquiryManagement from "./InquiryManagement";
import RegionManagement from "./RegionManagement";

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
  background: var(--background-color);
  color: var(--text-color);

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color);
  flex-wrap: wrap;
`;

const Title = styled.h1`
  color: var(--text-color);
  margin: 0;
  font-size: 1.5rem;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatCard = styled.div`
  background: var(--surface-color);
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px var(--shadow-color);
  border-left: 4px solid var(--primary-color);

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const StatTitle = styled.h3`
  color: var(--text-secondary-color);
  margin: 0 0 0.5rem 0;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (min-width: 768px) {
    font-size: 0.9rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--text-color);

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 1px solid var(--border-color);
  overflow-x: auto;
  padding-bottom: 0.5rem;

  @media (min-width: 768px) {
    overflow-x: visible;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 0.5rem 1rem;
  background: ${(props) =>
    props.$active ? "var(--primary-color)" : "transparent"};
  color: ${(props) =>
    props.$active ? "white" : "var(--text-secondary-color)"};
  border: none;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
  font-weight: ${(props) => (props.$active ? "600" : "500")};
  transition: all 0.2s;
  white-space: nowrap;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    gap: 0.75rem;
  }

  &:hover {
    background: ${(props) =>
      props.$active ? "var(--primary-color)" : "var(--background-color)"};
    opacity: ${(props) => (props.$active ? "0.9" : "0.8")};
  }
`;

const TabContent = styled.div`
  background: var(--surface-color);
  border-radius: 8px;
  box-shadow: 0 2px 10px var(--shadow-color);
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const UserSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--surface-color);
  border-radius: 8px;
  box-shadow: 0 2px 10px var(--shadow-color);

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const UserSearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  background: var(--background-color);
  color: var(--text-color);
  flex: 1;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-color);
  }
`;

const UserFilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  background: var(--background-color);
  color: var(--text-color);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const UserCount = styled.div`
  font-size: 14px;
  color: var(--text-secondary-color);
  text-align: center;

  @media (min-width: 768px) {
    text-align: left;
  }
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--surface-color);
  box-shadow: 0 2px 8px var(--shadow-color);
  transition: all 0.2s ease;
  flex-wrap: wrap;
  gap: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px var(--shadow-color);
    border-color: var(--primary-color);
  }

  @media (min-width: 768px) {
    flex-wrap: nowrap;
    gap: 1.5rem;
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex: 1;
  min-width: 0;
`;

const UserActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const UserStatus = styled.div<{ $status: "active" | "banned" | "inactive" }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => {
      switch (props.$status) {
        case "active":
          return "var(--success-color)";
        case "banned":
          return "var(--error-color)";
        case "inactive":
          return "var(--warning-color)";
        default:
          return "var(--secondary-color)";
      }
    }};
  }
`;

const UserAvatar = styled.div<{ avatar?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) =>
    props.avatar ? `url(${props.avatar})` : "var(--primary-color)"};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  flex-shrink: 0;
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const UserName = styled.div`
  font-weight: 600;
  color: var(--text-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserEmail = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary-color);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserRole = styled.span<{ role: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${(props) => {
    switch (props.role) {
      case "admin":
        return "var(--error-color)";
      case "approver":
        return "var(--warning-color)";
      case "user":
        return "var(--primary-color)";
      default:
        return "var(--secondary-color)";
    }
  }};
  color: white;
  flex-shrink: 0;
`;

const ActionButton = styled.button<{
  $variant: "success" | "danger" | "warning";
}>`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s;

  @media (min-width: 768px) {
    padding: 8px 16px;
    font-size: 14px;
    gap: 6px;
  }

  background: ${(props) => {
    switch (props.$variant) {
      case "success":
        return "var(--success-color)";
      case "danger":
        return "var(--error-color)";
      case "warning":
        return "var(--warning-button-bg, var(--warning-color))";
      default:
        return "var(--secondary-color)";
    }
  }};

  color: ${(props) =>
    props.$variant === "warning"
      ? "var(--warning-button-text, var(--warning-text))"
      : "white"};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--shadow-color);
  }
`;

// Table styles
const TableContainer = styled.div`
  background: var(--surface-color);
  border-radius: 12px;
  box-shadow: 0 4px 20px var(--shadow-color);
  overflow: hidden;
  margin-top: 1rem;
  overflow-x: auto;
`;

const TableHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-color);

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    max-width: 600px;
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  flex: 1;
  background: var(--surface-color);
  color: var(--text-color);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-color);
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 14px;
  background: var(--surface-color);
  color: var(--text-color);
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const TableHead = styled.thead`
  background: var(--background-color);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--border-color);

  &:hover {
    background: var(--background-color);
  }
`;

const TableHeaderCell = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  color: var(--text-secondary-color);
  font-size: 12px;

  @media (min-width: 768px) {
    padding: 1rem;
    font-size: 14px;
  }
`;

const TableCell = styled.td`
  padding: 0.75rem;
  vertical-align: middle;
  font-size: 12px;
  color: var(--text-color);

  @media (min-width: 768px) {
    padding: 1rem;
    font-size: 14px;
  }
`;

const StatusBadge = styled.span<{
  $status: "private" | "pending" | "approved" | "rejected" | "public";
}>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (min-width: 768px) {
    font-size: 12px;
  }

  background: ${(props) => {
    switch (props.$status) {
      case "private":
        return "var(--private-bg)";
      case "pending":
        return "var(--warning-bg)";
      case "approved":
        return "var(--info-bg)";
      case "rejected":
        return "var(--error-bg)";
      case "public":
        return "var(--success-bg)";
      default:
        return "var(--private-bg)";
    }
  }};

  color: ${(props) => {
    switch (props.$status) {
      case "private":
        return "var(--private-text)";
      case "pending":
        return "var(--warning-text)";
      case "approved":
        return "var(--info-text)";
      case "rejected":
        return "var(--error-text)";
      case "public":
        return "var(--success-text)";
      default:
        return "var(--private-text)";
    }
  }};
`;

const MapTitle = styled.div`
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 4px;
  font-size: 12px;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const MapAuthor = styled.div`
  font-size: 10px;
  color: var(--text-secondary-color);

  @media (min-width: 768px) {
    font-size: 12px;
  }
`;

const MapStats = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 10px;
  color: var(--text-secondary-color);
  margin-top: 4px;

  @media (min-width: 768px) {
    font-size: 12px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;

  @media (min-width: 768px) {
    gap: 0.5rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary-color);

  @media (min-width: 768px) {
    padding: 3rem;
  }
`;

const EmptyStateIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  opacity: 0.5;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

type TabType =
  | "overview"
  | "users"
  | "maps"
  | "regions"
  | "inquiries"
  | "settings";

const AdminDashboard: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState<
    "all" | "user" | "approver" | "admin"
  >("all");

  const [allMaps, setAllMaps] = useState<MapData[]>([]);
  const [filteredMaps, setFilteredMaps] = useState<MapData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "private" | "pending" | "approved" | "rejected" | "public"
  >("all");
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalMaps: 0,
    pendingApprovals: 0,
    activeUsers: 0,
  });

  useEffect(() => {
    if (!isAdmin()) return undefined;

    // Load admin data
    loadAdminData();
  }, [isAdmin]);

  // Filter users based on search term and role filter
  useEffect(() => {
    let filtered = users;

    // Filter by role
    if (userRoleFilter !== "all") {
      filtered = filtered.filter((user) => user.role === userRoleFilter);
    }

    // Filter by search term
    if (userSearchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.displayName
            .toLowerCase()
            .includes(userSearchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
    }

    setFilteredUsers(filtered);
  }, [users, userSearchTerm, userRoleFilter]);

  const loadAdminData = async () => {
    try {
      // Load real system statistics
      const systemStats = await getSystemStats();
      setStats(systemStats);

      // Load real users
      const allUsers = await getAllUsers();
      setUsers(allUsers);
      setFilteredUsers(allUsers);

      // Load all maps for admin dashboard
      const allMapsData = await getAllMapsForAdmin();
      setAllMaps(allMapsData);
      setFilteredMaps(allMapsData);
    } catch (error) {
      console.error("Error loading admin data:", error);
      toast.error("Failed to load admin data");
    }
  };

  // Filter maps based on search term and status filter
  useEffect(() => {
    let filtered = allMaps;

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((map) => {
        switch (statusFilter) {
          case "private":
            return (
              !map.isPublic &&
              !map.approvalRequested &&
              !map.isRejected &&
              !map.isApproved
            );
          case "pending":
            return map.approvalRequested && !map.isRejected && !map.isApproved;
          case "approved":
            return map.isApproved && !map.isPublic;
          case "rejected":
            return map.isRejected;
          case "public":
            return map.isPublic;
          default:
            return true;
        }
      });
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (map) =>
          map.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          map.userDisplayName
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          map.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredMaps(filtered);
  }, [allMaps, searchTerm, statusFilter]);

  if (!user || !isAdmin()) {
    return (
      <DashboardContainer>
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access the admin dashboard.</p>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <Header>
        <Shield size={32} color="#007bff" />
        <Title>Admin Dashboard</Title>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatTitle>Total Users</StatTitle>
          <StatValue>{stats.totalUsers}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Total Maps</StatTitle>
          <StatValue>{stats.totalMaps}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Pending Approvals</StatTitle>
          <StatValue>{stats.pendingApprovals}</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Active Users</StatTitle>
          <StatValue>{stats.activeUsers}</StatValue>
        </StatCard>
      </StatsGrid>

      <TabsContainer>
        <Tab
          $active={activeTab === "overview"}
          onClick={() => setActiveTab("overview")}
        >
          <BarChart3 size={16} />
          Overview
        </Tab>
        <Tab
          $active={activeTab === "users"}
          onClick={() => setActiveTab("users")}
        >
          <Users size={16} />
          Users
        </Tab>
        <Tab
          $active={activeTab === "maps"}
          onClick={() => setActiveTab("maps")}
        >
          <Map size={16} />
          Maps
        </Tab>
        <Tab
          $active={activeTab === "regions"}
          onClick={() => setActiveTab("regions")}
        >
          <Globe size={16} />
          Regions
        </Tab>
        <Tab
          $active={activeTab === "inquiries"}
          onClick={() => setActiveTab("inquiries")}
        >
          <MessageSquare size={16} />
          Inquiries
        </Tab>
        <Tab
          $active={activeTab === "settings"}
          onClick={() => setActiveTab("settings")}
        >
          <Settings size={16} />
          Settings
        </Tab>
      </TabsContainer>

      <TabContent>
        {activeTab === "overview" && (
          <div>
            <h3>System Overview</h3>
            <p>
              Welcome to the admin dashboard. Here you can manage users, approve
              maps, and monitor system statistics.
            </p>
            {/* Add more overview content */}
          </div>
        )}

        {activeTab === "users" && (
          <div>
            <h3>User Management</h3>

            <UserSearchContainer>
              <Search size={16} color="var(--text-secondary-color)" />
              <UserSearchInput
                type="text"
                placeholder="Search users by name or email..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
              />
              <Filter size={16} color="var(--text-secondary-color)" />
              <UserFilterSelect
                value={userRoleFilter}
                onChange={(e) => setUserRoleFilter(e.target.value as any)}
              >
                <option value="all">All Roles</option>
                <option value="user">Users</option>
                <option value="approver">Approvers</option>
                <option value="admin">Admins</option>
              </UserFilterSelect>
              <UserCount>
                {filteredUsers.length} user
                {filteredUsers.length !== 1 ? "s" : ""} found
              </UserCount>
            </UserSearchContainer>

            {filteredUsers.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>👥</EmptyStateIcon>
                <h4>No users found</h4>
                <p>Try adjusting your search or filter criteria.</p>
              </EmptyState>
            ) : (
              <UserList>
                {filteredUsers.map((user) => (
                  <UserCard key={user.id}>
                    <UserInfo>
                      <UserAvatar avatar={user.avatar}>
                        {!user.avatar &&
                          user.displayName.charAt(0).toUpperCase()}
                      </UserAvatar>
                      <UserDetails>
                        <UserName>{user.displayName}</UserName>
                        <UserEmail>{user.email}</UserEmail>
                        <UserStatus $status="active">
                          Active • Last seen recently
                        </UserStatus>
                      </UserDetails>
                      <UserRole role={user.role}>{user.role}</UserRole>
                    </UserInfo>
                    <UserActions>
                      <UserFilterSelect
                        value={user.role}
                        onChange={async (e) => {
                          const newRole = e.target.value as
                            | "user"
                            | "approver"
                            | "admin";
                          if (
                            window.confirm(
                              `Change ${user.displayName}'s role to ${newRole}?`
                            )
                          ) {
                            try {
                              await updateUserRole(user.id, newRole);
                              toast.success("User role updated successfully");
                              loadAdminData(); // Reload data
                            } catch (error) {
                              toast.error("Failed to update user role");
                            }
                          } else {
                            // Reset the select to the original value if cancelled
                            e.target.value = user.role;
                          }
                        }}
                        style={{
                          minWidth: "120px",
                          fontSize: "13px",
                          padding: "6px 10px",
                        }}
                      >
                        <option value="user">User</option>
                        <option value="approver">Approver</option>
                        <option value="admin">Admin</option>
                      </UserFilterSelect>
                      <ActionButton
                        $variant="danger"
                        onClick={async () => {
                          if (
                            window.confirm(
                              `Are you sure you want to ban ${user.displayName}?`
                            )
                          ) {
                            try {
                              await banUser(user.id);
                              toast.success("User banned successfully");
                              loadAdminData(); // Reload data
                            } catch (error) {
                              toast.error("Failed to ban user");
                            }
                          }
                        }}
                      >
                        <XCircle size={16} />
                        Ban
                      </ActionButton>
                      <ActionButton
                        $variant="danger"
                        onClick={async () => {
                          if (
                            window.confirm(
                              `⚠️  WARNING: This will permanently delete ${user.displayName}!\n\nThis action cannot be undone and will remove the user from both the authentication system and database.\n\nAre you absolutely sure?`
                            )
                          ) {
                            try {
                              await deleteUser(user.id);
                              toast.success("User deleted successfully");
                              loadAdminData(); // Reload data
                            } catch (error) {
                              toast.error("Failed to delete user");
                            }
                          }
                        }}
                        style={{
                          backgroundColor: "#dc3545",
                          borderColor: "#dc3545",
                        }}
                      >
                        <Trash2 size={16} />
                        Delete
                      </ActionButton>
                    </UserActions>
                  </UserCard>
                ))}
              </UserList>
            )}
          </div>
        )}

        {activeTab === "maps" && (
          <div>
            <h3>Map Management</h3>

            <TableContainer>
              <TableHeader>
                <SearchContainer>
                  <Search size={16} color="#666" />
                  <SearchInput
                    type="text"
                    placeholder="Search maps by name, author, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Filter size={16} color="#666" />
                  <FilterSelect
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                  >
                    <option value="all">All Maps</option>
                    <option value="private">Private</option>
                    <option value="pending">Pending Approval</option>
                    <option value="approved">Approved (Private)</option>
                    <option value="public">Public</option>
                    <option value="rejected">Rejected</option>
                  </FilterSelect>
                </SearchContainer>
                <div style={{ fontSize: "14px", color: "#666" }}>
                  {filteredMaps.length} map
                  {filteredMaps.length !== 1 ? "s" : ""} found
                </div>
              </TableHeader>

              {filteredMaps.length === 0 ? (
                <EmptyState>
                  <EmptyStateIcon>🗺️</EmptyStateIcon>
                  <h4>No maps found</h4>
                  <p>Try adjusting your search or filter criteria.</p>
                </EmptyState>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Map</TableHeaderCell>
                      <TableHeaderCell>Author</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Created</TableHeaderCell>
                      <TableHeaderCell>Stats</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <tbody>
                    {filteredMaps.map((map) => {
                      // Determine map status
                      let status:
                        | "private"
                        | "pending"
                        | "approved"
                        | "rejected"
                        | "public" = "private";
                      if (map.isPublic) {
                        status = "public";
                      } else if (map.isRejected) {
                        status = "rejected";
                      } else if (map.isApproved) {
                        status = "approved";
                      } else if (map.approvalRequested) {
                        status = "pending";
                      } else {
                        status = "private";
                      }

                      return (
                        <TableRow key={map.id}>
                          <TableCell>
                            <MapTitle>{map.name}</MapTitle>
                            {map.description && (
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  marginTop: "4px",
                                }}
                              >
                                {map.description.length > 50
                                  ? `${map.description.substring(0, 50)}...`
                                  : map.description}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <MapAuthor>{map.userDisplayName}</MapAuthor>
                          </TableCell>
                          <TableCell>
                            <StatusBadge $status={status}>
                              {status === "private" && "🔒 Private"}
                              {status === "pending" && "⏳ Pending"}
                              {status === "approved" && "✅ Approved"}
                              {status === "rejected" && "❌ Rejected"}
                              {status === "public" && "🌐 Public"}
                            </StatusBadge>
                          </TableCell>
                          <TableCell>
                            {new Date(map.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <MapStats>
                              <span>States: {map.states.length}</span>
                              <span>Likes: {map.likesCount}</span>
                            </MapStats>
                          </TableCell>
                          <TableCell>
                            <ActionButtons>
                              {/* Show approve button only for pending maps */}
                              {status === "pending" && (
                                <ActionButton
                                  $variant="success"
                                  onClick={async () => {
                                    try {
                                      await approveMap(map.id);
                                      toast.success(
                                        "Map approved successfully"
                                      );
                                      loadAdminData(); // Reload data
                                    } catch (error) {
                                      toast.error("Failed to approve map");
                                    }
                                  }}
                                >
                                  <CheckCircle size={14} />
                                  Approve
                                </ActionButton>
                              )}

                              {/* Show reject button for pending and approved maps */}
                              {(status === "pending" ||
                                status === "approved") && (
                                <ActionButton
                                  $variant="danger"
                                  onClick={async () => {
                                    const reason = prompt(
                                      "Please provide a reason for rejection (optional):"
                                    );
                                    try {
                                      await rejectMap(
                                        map.id,
                                        reason || undefined
                                      );
                                      toast.success(
                                        "Map rejected successfully"
                                      );
                                      loadAdminData(); // Reload data
                                    } catch (error) {
                                      toast.error("Failed to reject map");
                                    }
                                  }}
                                >
                                  <XCircle size={14} />
                                  Reject
                                </ActionButton>
                              )}

                              {/* Show view button for all maps */}
                              <ActionButton
                                $variant="warning"
                                onClick={() =>
                                  window.open(`/map/${map.id}`, "_blank")
                                }
                              >
                                <Eye size={14} />
                                View
                              </ActionButton>
                            </ActionButtons>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </TableContainer>
          </div>
        )}

        {activeTab === "regions" && <RegionManagement />}

        {activeTab === "inquiries" && <InquiryManagement />}

        {activeTab === "settings" && (
          <div>
            <h3>System Settings</h3>
            <p>Configure system-wide settings, limits, and permissions.</p>
            {/* Add settings form */}
          </div>
        )}
      </TabContent>
    </DashboardContainer>
  );
};

export default AdminDashboard;
